import { IconBase } from "components/base";
import { FileModel } from "interfaces/models";
import { isEqual } from "lodash-es";
import { lazy, memo } from "react";
import FieldPreviewWrapper from "../../../components/FieldPreviewWrapper";
import SelectFromGallery from "../../../components/SelectFromGallery";
import { FieldPreviewProps, OnChangeType } from "../../types";
import { DroppableBox } from "components/containers/InspectionFormat/containers/LayoutFrameBuilder";
import { SOURCE } from "components/containers/InspectionFormat/Content/SettingPinLayoutFrame/context/pinLayoutFrameReducer";
import CameraPlusIcon from "components/icon/CameraPlusIcon";
import ImageIcon from "components/icon/ImageIcon";
import CameraModal from "components/modal/CameraModal";
import ConfirmModal from "components/modal/ConfirmDeleteModal";
import ImageContainer from "./ImageContainer";
import ImageForm from "./ImageForm";
import useFieldImage, { ModalType } from "../useFieldImage";

const DrawToolModalContainer = lazy(
  () => import("../../../components/DrawToolModalContainer")
);

export interface ImageValue extends FileModel {}

interface ImagePreviewerProps extends FieldPreviewProps<ImageValue> {}

const ImagePreviewer: React.FC<ImagePreviewerProps> = (props) => {
  const {
    item,
    isLoading,
    isDraggable,
    index,
    allItemResultCount,
    prevItemResultCount,
    onChange,
  } = props;

  const {
    imageProps,
    modal,
    setModal,
    image,
    setImage,
    imageCaptured,
    disabled,
    isOpenImageForm,
    onOpenPhotoHistories,
    onOpenDrawToolModal,
    onDeleteImageHistory,
    onSelectFromGallery,
    onDraw,
    onCamera,
    pinSelected,
  } = useFieldImage({ ...props, isBlackboard: false });

  const handleDeleteImage = () => {
    setImage(undefined);
    setModal(null);
    onChange?.({}, { action: OnChangeType.DELETE_IMAGE });
  };

  if (isDraggable) {
    return (
      <FieldPreviewWrapper
        title={item.name}
        data-type={item.dataType}
        index={index}
        allItemResultCount={allItemResultCount}
        prevItemResultCount={prevItemResultCount}
      >
        <DroppableBox
          flex={1}
          name={item.name}
          icon={<ImageIcon />}
          id={item.id}
          source={SOURCE.INSPECTION_FORM}
        >
          <ImageForm
            isDisabled={disabled}
            cameraIcon={<CameraPlusIcon width="2.4rem" height="2.4rem" />}
            imageIcon={
              <IconBase
                icon="/img/icon-add-photo.svg"
                width="3rem"
                height="3rem"
                backgroundColor="#525252"
              />
            }
            onOpenCamera={onCamera}
            onSelectFile={onOpenDrawToolModal}
          />
        </DroppableBox>
      </FieldPreviewWrapper>
    );
  }

  return (
    <FieldPreviewWrapper
      title={item.name}
      data-type={item.dataType}
      data-value={image?.src}
      index={index}
      allItemResultCount={allItemResultCount}
      prevItemResultCount={prevItemResultCount}
    >
      {isOpenImageForm && (
        <ImageForm
          isDisabled={disabled}
          cameraIcon={<CameraPlusIcon width="2.4rem" height="2.4rem" />}
          imageIcon={
            <IconBase
              icon="/img/icon-add-photo.svg"
              width="2.4rem"
              height="2.4rem"
              backgroundColor="#525252"
            />
          }
          onOpenCamera={onCamera}
          onSelectFile={onOpenDrawToolModal}
        />
      )}

      {image?.src && (
        <ImageContainer
          isLoading={isLoading}
          isDisabled={disabled}
          imageProps={imageProps}
          onSelectFile={onOpenDrawToolModal}
          onOpenCamera={onCamera}
          onOpenPhotoHistories={onOpenPhotoHistories}
        />
      )}

      {pinSelected && (
        <>
          {modal === ModalType.GALLERY && (
            <SelectFromGallery
              isOpen={true}
              value={image?.src}
              pinId={pinSelected.id}
              itemResultId={item.id}
              onDeleteImage={onDeleteImageHistory}
              onChange={onSelectFromGallery}
              onClose={() => setModal(null)}
            />
          )}
          <DrawToolModalContainer
            isOpen={modal === ModalType.DRAW_IMAGE}
            image={image}
            setImage={onDraw}
            onClose={() => setModal(null)}
            imageCaptured={imageCaptured}
            pinId={pinSelected.id}
            itemResultId={item.id}
          />
        </>
      )}
      <ConfirmModal
        title="削除の確認"
        content="削除してよろしいですか。"
        isDelete
        buttonConfirm="削除する"
        isOpen={modal === ModalType.CONFIRM_DELETE}
        onClose={() => setModal(null)}
        onProcessing={handleDeleteImage}
      />
      {modal === ModalType.CAMERA && (
        <CameraModal
          isOpen={true}
          onClose={() => setModal(null)}
          onCapture={onOpenDrawToolModal}
        />
      )}
    </FieldPreviewWrapper>
  );
};

export default memo(ImagePreviewer, (prev, next) => isEqual(prev, next));
